import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'reducers';

/**
 * Memoized selector that combines all Ampol Cash Orders page data.
 * Prevents unnecessary re-renders by only updating when actual data changes.
 *
 * This selector consolidates data from:
 * - cashStore: Order list, card details, activation states
 * - userStore: User context, permissions, account information
 * - accountsStore: Account loading/error states
 * - activateCard: Single card activation states
 * - activateMultipleCards: Bulk activation states
 * - cardsStore: Global search functionality
 *
 * @returns Complete context object for AmpolCashOrders component
 */
export const selectAmpolCashOrdersPageContext = createSelector(
  [
    (state: RootState) => state.cashStore,
    (state: RootState) => state.userStore,
    (state: RootState) => state.accountsStore,
    (state: RootState) => state.activateCard,
    (state: RootState) => state.activateMultipleCards,
    (state: RootState) => state.cardsStore,
  ],
  (cashStore, userStore, accountsStore, activateCard, activateMultipleCards, cardsStore) => ({
    // Cash Store - Order List Data
    cashOrdersList: cashStore.cashOrdersList,
    cashOrdersListLoading: cashStore.cashOrdersListLoading,
    cashOrdersListError: cashStore.cashOrdersListError,
    cashOrdersListResultSize: (cashStore as any).cashOrdersListResultSize,

    // Cash Store - Card Details
    cashOrderCardDetails: cashStore.cashOrderCardDetails,
    cashOrderDetails: cashStore.cashOrderDetails,
    cashOrderCardDetailsPageNumber: cashStore.cashOrderCardDetailsPageNumber,

    // Cash Store - Activation States
    activateAllEnabled: cashStore.activateAllEnabled,
    activateAllCardsLoading: cashStore.activateAllCardsLoading,
    activateAllCardsLoaded: cashStore.activateAllCardsLoaded,

    // User Store - User Context
    loggedInUser: userStore.loggedInUser,
    assignedAccountNumber: userStore.assignedAccountNumber,
    assignedGrandfatherClientNumber: userStore.assignedGrandfatherClientNumber,

    // User Store - Permissions
    canReadWriteCash: userStore.canReadWriteCash,

    // Accounts Store - Loading States
    grandparentAccountLoading: accountsStore.grandparentAccountLoading,
    grandparentAccountError: accountsStore.grandparentAccountError,

    // Activate Card (Single) - States
    activateCardSingleIsLoading: activateCard.isLoading,
    activateCardSingleSuccess: activateCard.activateCardSuccess,

    // Activate Multiple Cards - States
    activateMultipleCardsLoaded: activateMultipleCards.activateCardSuccess,

    // Cards Store - Global Search
    globalSearchLoaded: cardsStore.globalSearchLoaded,
    globalSearchResult: cardsStore.globalSearchResult,
  })
);

/**
 * Type-safe hook for accessing Ampol Cash Orders context
 * Provides autocomplete and type checking for all properties
 */
export type AmpolCashOrdersContext = ReturnType<typeof selectAmpolCashOrdersPageContext>;
